import { type ChannelAlias, fromBackendChannel } from "../channel-map";
import { type ApiFetchContext, apiFetch } from "../client";

// Backend: GET /product/address/resolve?keyword=<url> → Result<ResolveResultVo>
// (ProductController is @RequestMapping("/product") + @GetMapping("/address/resolve"),
// so the full path is /product/address/resolve — omitting /product 404s.)
// ResolveResultVo = { spuNo: string; channel: string }  (channel is the backend
// enum: TAOBAO / 1688 / WEIDIAN / YESGO / ...). On a link it can't parse, the
// backend returns Result.error(...) which apiFetch surfaces as a thrown ApiError.

interface ResolveResultRaw {
  spuNo?: string | null;
  channel?: string | null;
}

export interface ResolvedProduct {
  channel: ChannelAlias;
  spuNo: string;
}

/**
 * Resolve a pasted source-platform product URL to a PDP target.
 *
 * Returns `null` (not a throw) for every "couldn't resolve" outcome — backend
 * error envelope, blank spuNo, or a channel the public frontend doesn't map
 * (e.g. YESGO) — so the caller can show one "contact support" message without a
 * try/catch. A thrown error is reserved for genuinely unexpected failures.
 */
export async function resolveProductUrl(
  ctx: ApiFetchContext,
  keyword: string,
): Promise<ResolvedProduct | null> {
  let raw: ResolveResultRaw;
  try {
    raw = await apiFetch<ResolveResultRaw>(
      ctx,
      `/product/address/resolve?keyword=${encodeURIComponent(keyword)}`,
    );
  } catch {
    // Backend Result.error() (unparseable link) → apiFetch throws. Treat as "no result".
    return null;
  }

  const backendChannel = raw.channel?.trim();
  const spuNo = raw.spuNo?.trim();
  if (!backendChannel || !spuNo) return null;

  let channel: ChannelAlias;
  try {
    channel = fromBackendChannel(backendChannel);
  } catch {
    // Backend resolved a channel the public frontend has no alias for (e.g. YESGO).
    return null;
  }

  return { channel, spuNo };
}
